import { useState, useMemo, useCallback, ReactNode, useEffect } from 'react';
import { formatUnits } from 'viem';
import {
  toHumanReadableTokenAmount,
  toRawTokenUnits,
} from '@/utils/tokenAmountUtils';
import {
  ChevronDown,
  ChevronLeft,
  FlaskConical,
  Loader2,
  Upload,
} from 'lucide-react';
import {
  Asset,
  buildRateLimitKey,
  nativeTokenDecimals,
  toChecksumAddress,
} from '@mls-wallet/shared-types';
import type { NetworkType } from '@mls-wallet/shared-types';
import {
  useTransactionBuilderStore,
  TransactionBuilderPageType,
  TransactionType,
  TransactionTypeEnum,
  type ContractTransactionImportSource,
} from '../../components/transaction-builder/useTransactionBuilderStore';
import { TransactionBuilderHeader } from '../../components/transaction-builder/TransactionBuilderHeader';
import { TransactionBuilderFooter } from '../../components/transaction-builder/TransactionBuilderFooter';
import { TransactionBuilderPolicyIndicator } from '../../components/transaction-builder/TransactionBuilderPolicyIndicator';
import { TokenSelectorDialog } from '../../components/transaction-builder/TokenSelectorDialog';
import { RecipientSelectorDialog } from '../../components/transaction-builder/RecipientSelectorDialog';
import type {
  Contract,
  DecodedContractTransactionPayload,
  NativeTokenValue,
  SelectedContractFunction,
} from '../../components/transaction-builder/types';
import { ContractAddressInput } from '../../components/transaction-builder/ContractAddressInput';
import { FunctionSelectorDialog } from '../../components/transaction-builder/FunctionSelectorDialog';
import {
  findContractFunctionBySelector,
  toSelectedContractFunction,
} from '../../components/transaction-builder/contract-function';
import {
  buildDefaultParameterStates,
  TransactionBuilderParameterState,
} from '../../components/transaction-builder/parameter-inputs';
import { ImportedAddressBookNotice } from '../../components/transaction-builder/ImportedAddressBookNotice';
import { ContractFunctionParameterInputs } from '../../components/transaction-builder/ContractFunctionParameterInputs';
import { collectAddressParameterValues } from '../../components/transaction-builder/parameter-inputs/collect-address-parameter-values';
import { useManualTransactionAddressValidation } from '../../components/transaction-builder/useManualTransactionAddressValidation';
import { useAccountAssets } from '@/hooks/account/useAccountAssets';
import { useAccountTokenUsdPrices } from '@/hooks/account/useAccountTokenUsdPrices';
import { useListAddressBookEntries } from '@/hooks/organization/use-list-address-book';
import { useAddressBookEntryByAddress } from '@/hooks/organization/use-address-book-entry-by-address';
import { useContractAbi } from '@/hooks/transaction/use-contract-abi';
import { ArrowLeftRightIcon } from '@/components/icons/ArrowLeftRightIcon';
import { FileJsonIcon } from '@/components/icons/FileJsonIcon';
import { DefaultTokenIcon } from '@/components/icons/DefaultTokenIcon';
import { formatAddress, formatUsdValue } from '@/lib/utils';
import { DecimalsButton } from '../../components/transaction-builder/parameter-inputs/DecimalsButton';
import { useAuthClient } from '@/lib/authClient';
import { WalletConnectTransactionDialog } from '../../components/transaction-builder/wallet-connect/WalletConnectTransactionDialog';
import { WalletConnectSheet } from '../../components/transaction-builder/wallet-connect/WalletConnectSheet';
import { useWalletConnect } from '../../components/transaction-builder/wallet-connect/useWalletConnect';
import { WalletConnectValueSourceIndicator } from '../../components/transaction-builder/wallet-connect/WalletConnectValueSourceIndicator';
import { WalletConnectIcon } from '@/components/icons/WalletConnectIcon';
import { RawTransactionImportDialog } from '../../components/transaction-builder/RawTransactionImportDialog';
import { JsonFileValueSourceIndicator } from '../../components/transaction-builder/JsonFileValueSourceIndicator';
import { UndecodedCalldataPanel } from '../../components/transaction-builder/UndecodedCalldataPanel';
import {
  policyHasConfiguredFunctions,
  type RawContractTransactionImport,
  type RawContractTransaction,
} from '../../components/transaction-builder/raw-transaction-import';
import { TransactionSimulationSheet } from '../../components/transaction-builder/transaction-simulation/TransactionSimulationSheet';
import {
  buildLiveContractInteractionDetails,
  buildSimulationRequestOrNull,
} from './transactionDetailsDraft';

/**
 * A styled row component for the transaction builder
 * Displays a label on the left and content on the right with space-between layout
 */
function DetailsRow({
  label,
  labelAccessory,
  children,
}: {
  label: string;
  labelAccessory?: ReactNode;
  children: ReactNode;
}): React.ReactElement {
  return (
    <div className="bg-neutral-50 border border-solid border-white rounded-full shadow-[0px_4px_6px_-3px_rgba(0,0,0,0.1)] w-full">
      <div className="flex flex-col items-stretch gap-2 justify-between pl-[21px] pr-[6px] py-[5px] w-full sm:flex-row sm:items-center sm:gap-0 [&>div]:max-w-full [&>button]:max-w-full">
        <div className="flex items-center gap-[10px]">
          <p className="font-medium text-[16px] text-black">{label}</p>
          {labelAccessory}
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * A toggle button for selecting between "Token Transfer" and "Contract Interaction"
 */
function TransactionTypeToggleButton({
  icon,
  label,
  isActive,
  onClick,
  disabled = false,
}: {
  icon: ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <button
      className={`flex-1 flex gap-[10px] items-center justify-center px-[12px] py-[5px] rounded-[31px] transition-colors ${
        isActive
          ? 'bg-white text-zinc-900 dark:text-white'
          : disabled
            ? 'bg-transparent text-zinc-300 cursor-not-allowed'
            : 'bg-transparent text-zinc-600 hover:bg-white/60 hover:text-zinc-900 active:bg-white/80 dark:text-zinc-400 dark:hover:bg-zinc-950/40 dark:hover:text-zinc-200 dark:active:bg-zinc-950/60'
      }`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {icon}
      <span className="font-medium text-[14px] leading-[20px] whitespace-nowrap">
        {label}
      </span>
    </button>
  );
}

/**
 * A dropdown button component for selectors (token, recipient, etc.)
 * Displays content on the left and a chevron icon on the right
 */
function DropdownButton({
  children,
  onClick,
  disabled = false,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <button
      className={`bg-white border border-solid border-zinc-100 rounded-[36px] w-[386px] transition-colors p-[5px] ${
        disabled
          ? 'cursor-not-allowed bg-zinc-50'
          : 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-200 dark:hover:border-slate-700'
      }`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      <div className="flex items-center justify-between pl-[5px] pr-[15px] py-[5px]">
        {children}
        <ChevronDown
          className={`w-[24px] h-[24px] ${
            disabled ? 'text-zinc-400' : 'text-black'
          }`}
        />
      </div>
    </button>
  );
}

function RecipientSummary({
  recipient,
  disabled = false,
}: {
  recipient: { address: string; label: string };
  disabled?: boolean;
}): React.ReactElement {
  const hasCustomLabel =
    recipient.label.trim().length > 0 &&
    recipient.label.toLowerCase() !== recipient.address.toLowerCase();
  const primaryTextClassName = disabled ? 'text-zinc-500' : 'text-zinc-900';
  const secondaryTextClassName = disabled ? 'text-zinc-400' : 'text-zinc-500';

  return (
    <div className="min-w-0 flex flex-1 items-center justify-between gap-4 text-left">
      {hasCustomLabel ? (
        <>
          <p
            className={`min-w-0 flex-1 truncate font-medium text-[14px] leading-[20px] ${primaryTextClassName}`}
          >
            {recipient.label}
          </p>
          <p
            className={`shrink-0 font-mono text-[12px] leading-[18px] ${secondaryTextClassName}`}
          >
            {formatAddress(recipient.address)}
          </p>
        </>
      ) : (
        <p
          className={`truncate font-mono text-[14px] leading-[20px] ${primaryTextClassName}`}
        >
          {formatAddress(recipient.address)}
        </p>
      )}
    </div>
  );
}

function recipientNeedsLabelHydration(recipient: {
  address: string;
  label: string;
}): boolean {
  return (
    recipient.label.trim().length === 0 ||
    recipient.label.toLowerCase() === recipient.address.toLowerCase()
  );
}

/**
 * Transaction details page for the transaction builder
 * Displays form for entering transaction details including transaction type,
 * token selection, amount, and recipient
 */
export function TransactionBuilderTransactionDetailsPage({
  embedded = false,
  isEditing = false,
  onSave,
}: {
  embedded?: boolean;
  isEditing?: boolean;
  onSave?: () => void;
} = {}): React.ReactElement {
  const [isSimulationOpen, setIsSimulationOpen] = useState(false);
  const editorSessionId = useTransactionBuilderStore(
    (state) => state.editorSessionId
  );
  const account = useTransactionBuilderStore((state) => state.account);
  const network = useTransactionBuilderStore((state) => state.network);
  const policy = useTransactionBuilderStore((state) => state.policy);
  const description = useTransactionBuilderStore((state) => state.description);
  const { user } = useAuthClient();
  const { assets, isLoading: isAssetsLoading } = useAccountAssets(
    account?.id || ''
  );
  const { tokenUsdPrices, isLoading: isTokenPricesLoading } =
    useAccountTokenUsdPrices(account?.id || '');
  const navigateForwardToPage = useTransactionBuilderStore(
    (state) => state.navigateForwardToPage
  );
  const storeTransactionType = useTransactionBuilderStore(
    (state) => state.transactionType
  );
  const storeTokenTransferDetails = useTransactionBuilderStore(
    (state) => state.tokenTransferDetails
  );
  const storeTokenTransferDraft = useTransactionBuilderStore(
    (state) => state.tokenTransferDraft
  );
  const setStoreTransactionType = useTransactionBuilderStore(
    (state) => state.setTransactionType
  );
  const setStoreTokenTransferDraft = useTransactionBuilderStore(
    (state) => state.setTokenTransferDraft
  );
  const setStoreTokenTransferDetails = useTransactionBuilderStore(
    (state) => state.setTokenTransferDetails
  );
  const storeContractInteractionDetails = useTransactionBuilderStore(
    (state) => state.contractInteractionDetails
  );
  const setStoreContractInteractionDetails = useTransactionBuilderStore(
    (state) => state.setContractInteractionDetails
  );
  const setStoreFunctionParameters = useTransactionBuilderStore(
    (state) => state.setFunctionParameters
  );
  const setStoreNativeTokenValue = useTransactionBuilderStore(
    (state) => state.setNativeTokenValue
  );
  const clearStoreRawTransaction = useTransactionBuilderStore(
    (state) => state.clearRawTransaction
  );
  const clearStoreFunctionData = useTransactionBuilderStore(
    (state) => state.clearContractInteractionFunctionData
  );
  const walletConnect = useWalletConnect();
  const [showWalletConnectSheet, setShowWalletConnectSheet] = useState(false);
  const [showRawTransactionImport, setShowRawTransactionImport] =
    useState(false);
  const [walletConnectPolicyGateError, setWalletConnectPolicyGateError] =
    useState<string | null>(null);

  /**
   * Initialize state from store data if available
   */
  const [transactionType, setTransactionType] = useState<TransactionType>(
    storeTransactionType || TransactionTypeEnum.TOKEN_TRANSFER
  );
  const storedTokenTransferDraft =
    storeTokenTransferDraft ??
    (storeTokenTransferDetails
      ? {
          displayAmount: storeTokenTransferDetails.displayAmount,
          recipient: storeTokenTransferDetails.recipient,
          asset: storeTokenTransferDetails.asset,
        }
      : undefined);
  // Initialize from store if available, otherwise null
  const [selectedToken, setSelectedToken] = useState<Asset | null>(() => {
    const storedAsset = storedTokenTransferDraft?.asset;
    if (!storedAsset) return null;

    return (
      assets?.find(
        (asset) => asset.tokenAddress === storedAsset.tokenAddress
      ) ?? storedAsset
    );
  });
  const [amount, setAmount] = useState<string>(
    storedTokenTransferDraft?.displayAmount || ''
  );
  const [selectedRecipient, setSelectedRecipient] = useState<{
    address: string;
    label: string;
  } | null>(storedTokenTransferDraft?.recipient || null);
  const [showTokenSelector, setShowTokenSelector] = useState(false);
  const [showRecipientSelector, setShowRecipientSelector] = useState(false);
  const [showFunctionSelector, setShowFunctionSelector] = useState(false);
  const isRecipientSelectorLoading = !policy || !network;
  const {
    entries: recipientAddressBookEntries,
    hasMore: hasMoreRecipientAddressBookEntries,
    isLoadingMore: isLoadingMoreRecipientAddressBookEntries,
    loadMore: loadMoreRecipientAddressBookEntries,
  } = useListAddressBookEntries({
    enabled:
      transactionType === TransactionTypeEnum.TOKEN_TRANSFER &&
      selectedRecipient !== null,
    pagination: { limit: 100 },
  });
  // Initialize contract from store data if available
  const [selectedContract, setSelectedContract] = useState<Contract | null>(
    storeContractInteractionDetails ?? null
  );
  const [selectedFunction, setSelectedFunction] =
    useState<SelectedContractFunction | null>(
      storeContractInteractionDetails?.selectedFunction || null
    );

  const [functionParameters, setFunctionParameters] = useState<
    TransactionBuilderParameterState[]
  >(storeContractInteractionDetails?.functionParameters || []);

  const [nativeTokenValue, setNativeTokenValue] = useState<string>(
    storeContractInteractionDetails?.nativeTokenValue?.value || ''
  );
  const [importedRawTransaction, setImportedRawTransaction] =
    useState<RawContractTransaction | null>(
      storeContractInteractionDetails?.rawTransaction ?? null
    );
  const [importSource, setImportSource] = useState<
    ContractTransactionImportSource | undefined
  >(storeContractInteractionDetails?.importSource);
  const [rawTransactionImportVersion, setRawTransactionImportVersion] =
    useState(0);
  const isUndecodedRawTransaction = Boolean(
    importedRawTransaction && !selectedFunction
  );
  const [isContractFromWalletConnect, setIsContractFromWalletConnect] =
    useState(false);
  const [isFunctionFromWalletConnect, setIsFunctionFromWalletConnect] =
    useState(false);
  // Hydrate payable decimals from store before draft sync can mirror them back.
  const [nativeTokenDecimalsSelected, setNativeTokenDecimalsSelected] =
    useState<number>(
      storeContractInteractionDetails?.nativeTokenValue?.decimals ??
        nativeTokenDecimals
    );
  const isTokenPricingLoading = isAssetsLoading || isTokenPricesLoading;
  const contractAddressForLookup =
    transactionType === TransactionTypeEnum.CONTRACT_INTERACTION
      ? (selectedContract?.address ?? null)
      : null;
  const importedContractAddress = importSource
    ? contractAddressForLookup
    : null;
  const {
    entry: importedContractAddressBookEntry,
    isLoading: isImportedContractAddressBookLoading,
    error: importedContractAddressBookError,
  } = useAddressBookEntryByAddress(importedContractAddress);
  const {
    entries: contractAddressBookEntries,
    hasMore: hasMoreContractAddressBookEntries,
    isLoadingMore: isLoadingMoreContractAddressBookEntries,
    loadMore: loadMoreContractAddressBookEntries,
  } = useListAddressBookEntries({
    enabled: contractAddressForLookup !== null,
    type: 'CONTRACT',
    pagination: { limit: 100 },
  });

  // ContractAddressInput may return a new object when its ABI data has not
  // changed. Compare an order-independent selector/mutability fingerprint so
  // equivalent results reuse the current object and do not retrigger effects.
  // The selector identifies overloads; mutability affects the value UI.
  const handleContractLoaded = useCallback((contract: Contract) => {
    setSelectedContract((currentContract) => {
      if (!currentContract) return contract;

      const currentFunctionKey = [
        ...currentContract.implementationFunctions,
        ...currentContract.proxyFunctions,
      ]
        .map(
          ({ selector, stateMutability }) =>
            `${selector.toLowerCase()}:${stateMutability}`
        )
        .sort()
        .join(',');
      const nextFunctionKey = [
        ...contract.implementationFunctions,
        ...contract.proxyFunctions,
      ]
        .map(
          ({ selector, stateMutability }) =>
            `${selector.toLowerCase()}:${stateMutability}`
        )
        .sort()
        .join(',');

      if (
        currentContract.address.toLowerCase() ===
          contract.address.toLowerCase() &&
        currentFunctionKey === nextFunctionKey
      ) {
        return currentContract;
      }

      return contract;
    });
  }, []);

  const clearStoreContractInteractionDetails = useTransactionBuilderStore(
    (state) => state.clearContractInteractionDetails
  );

  const handleContractCleared = useCallback(() => {
    setSelectedContract(null);
    setSelectedFunction(null);
    setFunctionParameters([]);
    setNativeTokenValue('');
    setImportedRawTransaction(null);
    setImportSource(undefined);
    setIsContractFromWalletConnect(false);
    setIsFunctionFromWalletConnect(false);
    clearStoreContractInteractionDetails();
  }, [clearStoreContractInteractionDetails]);

  useEffect(() => {
    // Persist transaction type immediately so remounts restore the active form.
    setStoreTransactionType(transactionType);
  }, [setStoreTransactionType, transactionType]);

  useEffect(() => {
    // Keep partial transfer input in store before it becomes final on Continue.
    if (transactionType !== TransactionTypeEnum.TOKEN_TRANSFER) {
      return;
    }

    setStoreTokenTransferDraft({
      displayAmount: amount,
      recipient: selectedRecipient,
      asset: selectedToken,
    });
  }, [
    amount,
    selectedRecipient,
    selectedToken,
    setStoreTokenTransferDraft,
    transactionType,
  ]);

  useEffect(() => {
    // Keep contract interaction input in store so route remounts do not drop it.
    if (
      transactionType !== TransactionTypeEnum.CONTRACT_INTERACTION ||
      !selectedContract
    ) {
      return;
    }

    setStoreContractInteractionDetails(
      buildLiveContractInteractionDetails({
        contract: selectedContract,
        selectedFunction,
        functionParameters,
        nativeTokenValue,
        nativeTokenDecimals: nativeTokenDecimalsSelected,
        rawTransaction: importedRawTransaction,
        importSource,
      })
    );
  }, [
    functionParameters,
    importedRawTransaction,
    importSource,
    nativeTokenDecimalsSelected,
    nativeTokenValue,
    selectedContract,
    selectedFunction,
    setStoreContractInteractionDetails,
    transactionType,
  ]);

  // Fetch contract ABI when a contract is selected
  const {
    contractDisplayName: fetchedContractDisplayName,
    implementationFunctions,
    proxyFunctions,
    isLoading: isLoadingAbi,
  } = useContractAbi({
    address: selectedContract?.address ?? null,
    networkType: network ?? null,
  });

  const selectedContractAddressBookEntry = useMemo(() => {
    if (!selectedContract?.address) {
      return undefined;
    }

    return contractAddressBookEntries.find(
      (entry) =>
        entry.address.toLowerCase() === selectedContract.address.toLowerCase()
    );
  }, [contractAddressBookEntries, selectedContract?.address]);

  useEffect(() => {
    if (
      !contractAddressForLookup ||
      selectedContractAddressBookEntry ||
      !hasMoreContractAddressBookEntries ||
      isLoadingMoreContractAddressBookEntries
    ) {
      return;
    }

    void loadMoreContractAddressBookEntries();
  }, [
    contractAddressForLookup,
    hasMoreContractAddressBookEntries,
    isLoadingMoreContractAddressBookEntries,
    loadMoreContractAddressBookEntries,
    selectedContractAddressBookEntry,
  ]);

  // Update selected contract with fetched ABI data when it becomes available.
  // This populates functions for user-entered addresses (not mock contracts).
  useEffect(() => {
    if (!isLoadingAbi && selectedContract) {
      const hasNoFunctions =
        selectedContract.implementationFunctions.length === 0 &&
        selectedContract.proxyFunctions.length === 0;
      const hasFetchedData =
        (implementationFunctions?.length ?? 0) > 0 ||
        (proxyFunctions?.length ?? 0) > 0 ||
        fetchedContractDisplayName;

      if (hasNoFunctions && hasFetchedData) {
        setSelectedContract({
          ...selectedContract,
          contractName:
            selectedContract.contractName ||
            selectedContractAddressBookEntry?.name ||
            fetchedContractDisplayName ||
            selectedContract.contractName,
          implementationFunctions: implementationFunctions ?? [],
          proxyFunctions: proxyFunctions ?? [],
        });
      }
    }
  }, [
    implementationFunctions,
    proxyFunctions,
    fetchedContractDisplayName,
    isLoadingAbi,
    selectedContractAddressBookEntry?.name,
    selectedContract,
  ]);

  useEffect(() => {
    if (
      !selectedContract ||
      !selectedContractAddressBookEntry?.name ||
      selectedContract.contractName === selectedContractAddressBookEntry.name
    ) {
      return;
    }

    setSelectedContract({
      ...selectedContract,
      contractName: selectedContractAddressBookEntry.name,
    });
  }, [selectedContract, selectedContractAddressBookEntry?.name]);

  const functionAbiInputs = useMemo(() => {
    if (!selectedFunction) return [];
    // Pass through the full parameter structure including components for tuples
    return selectedFunction.inputs.map((input, idx) => ({
      ...input,
      name: input.name || `param${idx + 1}`,
    }));
  }, [selectedFunction]);

  /**
   * Initialize function parameters to match the selected function ABI
   * so each rendered input writes to its own index on first change.
   *
   * This effect runs whenever selectedFunction changes to ensure parameters
   * are always properly initialized for the current function.
   */
  useEffect(() => {
    if (!selectedFunction) {
      setFunctionParameters([]);
      // A raw import clears the selected function as it enters this effect.
      // Preserve the raw store fields it just wrote instead of resetting them.
      if (!importedRawTransaction) {
        clearStoreFunctionData();
      }
      setNativeTokenValue('');
      setStoreNativeTokenValue(undefined);
      return;
    }

    // Only reinitialize if parameters are empty or don't match function
    // This preserves store values on mount but clears on function change
    if (
      functionParameters.length === 0 ||
      functionParameters.length !== functionAbiInputs.length
    ) {
      const defaults = buildDefaultParameterStates(functionAbiInputs);
      setFunctionParameters(defaults);
      setStoreFunctionParameters(defaults);
    }
  }, [
    selectedFunction,
    functionAbiInputs,
    functionParameters.length,
    importedRawTransaction,
    setStoreFunctionParameters,
    clearStoreFunctionData,
    setStoreNativeTokenValue,
  ]);

  /**
   * Filter assets to only show those available on the selected network
   */
  const availableTokens = useMemo(() => {
    if (!assets || !network) {
      return [];
    }

    return assets.filter((asset) =>
      asset.balancesByNetwork.some((balance) => balance.network === network)
    );
  }, [assets, network]);

  // ============ POLICY RESTRICTION LOGIC ============

  /**
   * Determine which transaction types are allowed by the policy
   */
  const allowedTransactionTypes = useMemo(() => {
    // Case: no policy, allow both transaction types
    if (!policy)
      return [
        TransactionTypeEnum.TOKEN_TRANSFER,
        TransactionTypeEnum.CONTRACT_INTERACTION,
      ];

    switch (policy.transactionType) {
      case 'TOKEN_TRANSFER':
        return [TransactionTypeEnum.TOKEN_TRANSFER];
      case 'CONTRACT_INTERACTION':
        return [TransactionTypeEnum.CONTRACT_INTERACTION];
      case 'ANY':
      default:
        return [
          TransactionTypeEnum.TOKEN_TRANSFER,
          TransactionTypeEnum.CONTRACT_INTERACTION,
        ];
    }
  }, [policy]);

  /**
   * Check if a specific transaction type is disabled
   */
  const isTransactionTypeDisabled = (type: TransactionType) => {
    return !allowedTransactionTypes.includes(type as TransactionTypeEnum);
  };

  const isContractInteractionAllowedByPolicy = useMemo(() => {
    return allowedTransactionTypes.includes(
      TransactionTypeEnum.CONTRACT_INTERACTION
    );
  }, [allowedTransactionTypes]);

  /**
   * Get allowed destination addresses from policy (empty = any destination allowed)
   */
  const allowedDestinations = useMemo(() => {
    // Case: no policy or token transfer condition, return empty array
    if (
      !policy ||
      policy.transactionType !== 'TOKEN_TRANSFER' ||
      !policy.tokenTransferCondition
    )
      return [];
    return policy.tokenTransferCondition.destinationAddresses || [];
  }, [policy]);

  /**
   * Get amount threshold from policy (null = no limit)
   * Converts raw token units to human-readable for comparison with user input
   */
  const amountThreshold = useMemo(() => {
    // Case: No policy, not a token transfer policy, or no tokenTransferCondition
    if (
      !policy ||
      policy.transactionType !== 'TOKEN_TRANSFER' ||
      !policy.tokenTransferCondition
    )
      return null;

    const rawThreshold = policy.tokenTransferCondition.amountThreshold;
    if (!rawThreshold) return null;

    // Convert raw token units to human-readable format
    const decimals = policy.tokenTransferCondition.tokenDecimals;
    if (decimals == null) return rawThreshold; // Fallback if no decimals

    return toHumanReadableTokenAmount(rawThreshold, decimals);
  }, [policy]);

  /**
   * Filter available tokens based on policy restrictions
   * Uses tokenId from policy to match against asset.id
   */
  const policyFilteredTokens = useMemo(() => {
    // Case: no policy or token transfer condition, return all tokens
    if (
      !policy ||
      policy.transactionType !== 'TOKEN_TRANSFER' ||
      !policy.tokenTransferCondition
    )
      return availableTokens;

    const allowedTokenId = policy.tokenTransferCondition.tokenId;
    if (!allowedTokenId) return availableTokens; // null = any token allowed

    // Filter assets by tokenId (asset.id matches the tokenId)
    return availableTokens.filter((asset) => asset.id === allowedTokenId);
  }, [availableTokens, policy]);

  /**
   * Determine if token selector should be disabled (only one token allowed and selected)
   */
  const isTokenSelectorDisabled = useMemo(() => {
    return policyFilteredTokens.length === 1 && selectedToken !== null;
  }, [policyFilteredTokens.length, selectedToken]);

  /**
   * Determine if recipient selector should be disabled (only one destination allowed and selected)
   */
  const isRecipientSelectorDisabled = useMemo(() => {
    return allowedDestinations.length === 1 && selectedRecipient !== null;
  }, [allowedDestinations.length, selectedRecipient]);

  /**
   * Extract the single contract address from policy's contract interaction condition.
   *
   * MVP SCOPE: Each CONTRACT_INTERACTION policy has at most ONE contract address.
   * The backend schema still supports multiple, but we enforce single-contract
   * on the frontend for MVP. Throws if more than one contract is found.
   *
   * Returns:
   * - string: The single contract address if policy specifies one
   * - null: Policy allows any contract (no condition set) - uses free-form input
   * - undefined: Policy is not CONTRACT_INTERACTION type
   */
  const policyContractAddress = useMemo((): string | null | undefined => {
    if (!policy) {
      return undefined;
    }

    // For ANY policy type, when building contract interaction,
    // there are no contract restrictions - allow any contract
    if (policy.transactionType === 'ANY') {
      return null;
    }

    if (policy.transactionType !== 'CONTRACT_INTERACTION') {
      return undefined; // Not relevant (e.g., TOKEN_TRANSFER policy)
    }

    // Get the first contract address from the condition (single contract per policy)
    const address = policy.contractInteractionCondition?.[0]?.contractAddress;
    // Return null if no contract specified (allows any contract - free-form input)
    return address && address.length > 0 ? address : null;
  }, [policy]);

  const policyAllowedFunctionsForImport = useMemo(() => {
    if (policy?.transactionType !== 'CONTRACT_INTERACTION') return [];

    const condition = policy.contractInteractionCondition?.[0];
    if (!condition) return [];

    return [...condition.proxyFunctions, ...condition.implementationFunctions];
  }, [policy]);

  const policyAllowsUndecodedCalldata = useMemo(() => {
    if (!policy) return false;
    if (policy.transactionType === 'ANY') return true;
    if (policy.transactionType !== 'CONTRACT_INTERACTION') return false;

    return !policyHasConfiguredFunctions(policy.contractInteractionCondition);
  }, [policy]);

  /**
   * Fetch ABI for the policy-specified contract.
   * Only fetches when policy specifies a contract address.
   */
  const {
    contractDisplayName: policyContractDisplayName,
    implementationFunctions: policyImplementationFunctions,
    proxyFunctions: policyProxyFunctions,
    isLoading: isPolicyContractLoading,
    errorCode: policyContractErrorCode,
    error: policyContractNetworkError,
  } = useContractAbi(
    policyContractAddress && network
      ? { address: policyContractAddress, networkType: network }
      : null
  );

  const policyContractAddressBookEntry = useMemo(() => {
    if (!policyContractAddress) {
      return undefined;
    }

    return contractAddressBookEntries.find(
      (entry) =>
        entry.address.toLowerCase() === policyContractAddress.toLowerCase()
    );
  }, [contractAddressBookEntries, policyContractAddress]);

  /**
   * Build Contract object from policy condition + fetched ABI data.
   *
   * Returns:
   * - Contract object: When policy specifies a contract and ABI is loaded
   * - null: When policy allows any contract (free-form input case)
   * - undefined: When loading or not a CONTRACT_INTERACTION policy
   */
  const policyContract = useMemo((): Contract | null | undefined => {
    // Case: Not a contract interaction policy
    if (!policy) {
      return undefined;
    }

    // Case: ANY policy type allows any contract
    if (policy.transactionType === 'ANY') {
      return null;
    }

    // Case: Not a CONTRACT_INTERACTION policy
    if (policy.transactionType !== 'CONTRACT_INTERACTION') {
      return undefined;
    }

    // Case: Policy allows any contract (free-form input)
    if (policyContractAddress === null) {
      return null;
    }

    // Case: Still loading ABI
    if (isPolicyContractLoading) {
      return undefined;
    }

    // Case: ABI fetch failed
    if (policyContractErrorCode || policyContractNetworkError) {
      return undefined;
    }

    const condition = policy.contractInteractionCondition?.[0];
    if (!condition || !policyContractDisplayName) {
      return undefined;
    }

    return {
      address: condition.contractAddress,
      contractName:
        policyContractAddressBookEntry?.name || policyContractDisplayName,
      implementationFunctions: policyImplementationFunctions,
      proxyFunctions: policyProxyFunctions,
      allowedProxyFunctions: condition.proxyFunctions,
      allowedImplementationFunctions: condition.implementationFunctions,
    };
  }, [
    policy,
    policyContractAddress,
    policyContractAddressBookEntry?.name,
    policyContractDisplayName,
    policyImplementationFunctions,
    policyProxyFunctions,
    isPolicyContractLoading,
    policyContractErrorCode,
    policyContractNetworkError,
  ]);

  /**
   * Error message if policy contract ABI fetch failed.
   */
  const policyContractError = useMemo((): string | null => {
    if (isPolicyContractLoading || !policyContractAddress) {
      return null;
    }
    if (policyContractErrorCode) {
      return 'Failed to fetch contract ABI';
    }
    if (policyContractNetworkError) {
      return 'Failed to fetch contract ABI';
    }
    return null;
  }, [
    policyContractAddress,
    isPolicyContractLoading,
    policyContractErrorCode,
    policyContractNetworkError,
  ]);
  const shouldShowFunctionRow = Boolean(
    selectedContract && !policyContractError && !isUndecodedRawTransaction
  );

  /**
   * Get allowed policy functions for the currently selected contract.
   * Empty array = any function allowed.
   */
  const allowedFunctions = useMemo(() => {
    if (!selectedContract) return [];

    // Preserve selectors so policies can distinguish overloaded names.
    return [
      ...selectedContract.allowedProxyFunctions,
      ...selectedContract.allowedImplementationFunctions,
    ];
  }, [selectedContract]);

  /**
   * Get the name of the dapp that sent the incoming transaction
   * by matching the transaction topic to the dapp topic
   */
  const incomingTransactionDappName = useMemo(() => {
    const transactionTopic =
      walletConnect.transactionData?.topic ?? walletConnect.activeRequestTopic;
    if (!transactionTopic) {
      return undefined;
    }

    return walletConnect.dappData.find(
      (dapp) => dapp.topic === transactionTopic
    )?.name;
  }, [
    walletConnect.transactionData,
    walletConnect.activeRequestTopic,
    walletConnect.dappData,
  ]);

  /**
   * Handle the policy gate error for the incoming transaction
   */
  useEffect(() => {
    // Case: no transaction data, so no policy gate error
    if (!walletConnect.transactionData) {
      setWalletConnectPolicyGateError(null);
      return;
    }

    // Case: the transaction is allowed by the policy, so no policy gate error
    if (isContractInteractionAllowedByPolicy) {
      setWalletConnectPolicyGateError(null);
      return;
    }

    setWalletConnectPolicyGateError(
      'The selected policy does not allow contract interaction imports from WalletConnect.'
    );

    void walletConnect.rejectTransactionRequest();
  }, [
    isContractInteractionAllowedByPolicy,
    walletConnect.transactionData,
    walletConnect.rejectTransactionRequest,
  ]);

  /**
   * Determine if function selector should be disabled (only one function allowed and selected)
   */
  const isFunctionSelectorDisabled = useMemo(() => {
    const onlyAllowedFunction = allowedFunctions[0];
    return Boolean(
      allowedFunctions.length === 1 &&
        onlyAllowedFunction &&
        selectedFunction?.selector.toLowerCase() ===
          onlyAllowedFunction.selector.toLowerCase()
    );
  }, [allowedFunctions, selectedFunction]);

  // ============ AUTO-SELECTION EFFECTS ============

  /**
   * Auto-select transaction type if only one is allowed
   */
  useEffect(() => {
    if (allowedTransactionTypes.length === 1 && allowedTransactionTypes[0]) {
      setTransactionType(allowedTransactionTypes[0]);
    }
  }, [allowedTransactionTypes]);

  /**
   * Auto-select token if policy restricts to a single token
   */
  useEffect(() => {
    if (
      policyFilteredTokens.length === 1 &&
      !selectedToken &&
      policyFilteredTokens[0]
    ) {
      setSelectedToken(policyFilteredTokens[0]);
    }
  }, [policyFilteredTokens, selectedToken]);

  /**
   * Auto-select recipient if policy restricts to a single destination
   */
  useEffect(() => {
    if (allowedDestinations.length === 1 && !selectedRecipient) {
      const singleAddress = allowedDestinations[0];
      if (singleAddress) {
        setSelectedRecipient({
          address: singleAddress,
          label: '',
        });
      }
    }
  }, [allowedDestinations, selectedRecipient]);

  useEffect(() => {
    if (
      !selectedRecipient ||
      !recipientNeedsLabelHydration(selectedRecipient)
    ) {
      return;
    }

    const matchingEntry = recipientAddressBookEntries.find(
      (entry) =>
        entry.address.toLowerCase() === selectedRecipient.address.toLowerCase()
    );

    if (!matchingEntry) {
      return;
    }

    setSelectedRecipient({
      address: selectedRecipient.address,
      label: matchingEntry.name,
    });
  }, [recipientAddressBookEntries, selectedRecipient]);

  useEffect(() => {
    if (
      !selectedRecipient ||
      !recipientNeedsLabelHydration(selectedRecipient)
    ) {
      return;
    }

    const hasMatchingEntry = recipientAddressBookEntries.some(
      (entry) =>
        entry.address.toLowerCase() === selectedRecipient.address.toLowerCase()
    );

    if (
      hasMatchingEntry ||
      !hasMoreRecipientAddressBookEntries ||
      isLoadingMoreRecipientAddressBookEntries
    ) {
      return;
    }

    void loadMoreRecipientAddressBookEntries();
  }, [
    hasMoreRecipientAddressBookEntries,
    isLoadingMoreRecipientAddressBookEntries,
    loadMoreRecipientAddressBookEntries,
    recipientAddressBookEntries,
    selectedRecipient,
  ]);

  /**
   * Auto-select contract when policy specifies one.
   * MVP: Policy can only have one contract, so auto-select it when available.
   * Also update when policy contract changes (e.g., user switches policies).
   */
  useEffect(() => {
    if (!policyContract) return;

    // Auto-select when nothing is selected, or update if policy contract changed
    const shouldUpdate =
      !selectedContract || selectedContract.address !== policyContract.address;

    if (shouldUpdate) {
      setSelectedContract(policyContract);
      // Clear function selection when contract changes
      setSelectedFunction(null);
      setFunctionParameters([]);
      setImportedRawTransaction(null);
      setImportSource(undefined);
      clearStoreFunctionData();
      setNativeTokenValue('');
      setStoreNativeTokenValue(undefined);
    }
  }, [
    policyContract,
    selectedContract,
    clearStoreFunctionData,
    setStoreNativeTokenValue,
  ]);

  /**
   * Auto-select function if policy restricts to a single function
   * The parameter-initialization effect builds defaults after selection.
   */
  useEffect(() => {
    if (allowedFunctions.length === 1 && selectedContract) {
      const allFunctions = [
        ...selectedContract.implementationFunctions,
        ...selectedContract.proxyFunctions,
      ];
      const allowedFunction = allowedFunctions[0];
      if (!allowedFunction) return;

      if (
        selectedFunction?.selector.toLowerCase() ===
        allowedFunction.selector.toLowerCase()
      ) {
        return;
      }

      const func = findContractFunctionBySelector(
        allFunctions,
        allowedFunction.selector
      );

      if (func) {
        // Replace any stale overload state before parameter initialization runs.
        setFunctionParameters([]);
        setImportedRawTransaction(null);
        setImportSource(undefined);
        clearStoreFunctionData();
        setNativeTokenValue('');
        setStoreNativeTokenValue(undefined);
        setSelectedFunction(toSelectedContractFunction(func));
      }
    }
  }, [
    allowedFunctions,
    clearStoreFunctionData,
    selectedContract,
    selectedFunction,
    setStoreNativeTokenValue,
  ]);

  /**
   * Get balance and USD value for a specific asset on the selected network
   */
  const getAssetNetworkInfo = (asset: Asset) => {
    // Case: No network selected, return 0 for both balance and USD value
    if (!network) {
      return { balance: 0, usdValue: 0 };
    }

    const networkBalance = asset.balancesByNetwork.find(
      (b) => b.network === network
    );

    // Case: No balance found for the network, return 0 for both balance and USD value
    if (!networkBalance) {
      return { balance: 0, usdValue: 0 };
    }

    const tokenAmount = parseFloat(
      formatUnits(BigInt(networkBalance.balance), asset.decimals)
    );
    const tokenPrice = Number(
      tokenUsdPrices.find(
        (tokenUsdPrice) => tokenUsdPrice.contractTokenId === asset.id
      )?.realUsdValue?.price ?? NaN
    );
    const usdValue =
      isNaN(tokenAmount) || !Number.isFinite(tokenPrice)
        ? null
        : tokenAmount * tokenPrice;

    return { balance: tokenAmount, usdValue };
  };

  /**
   * Calculate USD value of entered amount
   */
  const amountUsdValue = useMemo(() => {
    if (!selectedToken || !amount || isNaN(parseFloat(amount))) {
      return 0;
    }

    const tokenPrice = Number(
      tokenUsdPrices.find(
        (tokenUsdPrice) => tokenUsdPrice.contractTokenId === selectedToken.id
      )?.realUsdValue?.price ?? NaN
    );
    return Number.isFinite(tokenPrice) ? parseFloat(amount) * tokenPrice : null;
  }, [selectedToken, amount, tokenUsdPrices]);

  /**
   * Format token balance for display
   */
  const formatBalance = (balance: number) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 6,
    }).format(balance);
  };

  function handleParameterChange(
    index: number,
    newParameterState: TransactionBuilderParameterState
  ): void {
    // Copy parameters to a new array object to cause state change
    const copiedParameters: TransactionBuilderParameterState[] = [
      ...functionParameters,
    ];

    // Update changed parameter in copied array
    copiedParameters[index] = newParameterState;

    // Update parameters array in local state and store
    setFunctionParameters(copiedParameters);
    if (functionParameters[index]?.value !== newParameterState.value) {
      setImportedRawTransaction(null);
      clearStoreRawTransaction();
    }
    setStoreFunctionParameters(copiedParameters);
  }

  /**
   * For token transfer policies with rate limits,
   * this returns the usage in the current window
   *
   * Keys needed to identify the usage are:
   * - initiator
   * - source account
   * - destination
   */
  const selectedLimitationUsage = useMemo(() => {
    // Case: Not a rate limited token transfer policy, return null
    if (
      !policy ||
      policy.transactionType !== 'TOKEN_TRANSFER' ||
      !policy.limitationUsage
    ) {
      return null;
    }
    const limitation = policy.limitation;
    const threshold = policy.tokenTransferCondition?.amountThreshold;

    // Case: Missing limitation configuration, return null
    if (!limitation || !threshold) {
      return null;
    }

    // Case: Per-initiator policies require a selected initiator to compute usage.
    if (limitation.initiatorScope !== 'ALL_ITEMS' && !user) {
      return null;
    }

    // Case: Per-source-account policies require a selected source account to compute usage.
    if (limitation.sourceAccountScope !== 'ALL_ITEMS' && !account) {
      return null;
    }

    // Case: Per-destination policies require a selected recipient to compute usage.
    if (limitation.destinationScope !== 'ALL_ITEMS' && !selectedRecipient) {
      return null;
    }

    const rateLimitKey = buildRateLimitKey({
      limitation,
      ...(user?.id !== undefined ? { initiatorId: user.id } : {}),
      ...(account?.id !== undefined ? { sourceAccountId: account.id } : {}),
      ...(selectedRecipient?.address
        ? { destination: toChecksumAddress(selectedRecipient.address) }
        : {}),
    });

    const found =
      policy.limitationUsage.buckets.find(
        (bucket) =>
          bucket.key.initiatorKey === rateLimitKey.initiatorKey &&
          bucket.key.sourceAccountKey === rateLimitKey.sourceAccountKey &&
          bucket.key.destinationKey.toLowerCase() ===
            rateLimitKey.destinationKey.toLowerCase()
      ) ?? null;
    if (found) {
      return found;
    }
    return {
      key: rateLimitKey,
      usedAmount: '0',
      remainingAmount: policy.limitationUsage.amountThreshold,
      usedAmountRaw: '0',
      remainingAmountRaw: threshold,
    };
  }, [account, policy, selectedRecipient, user]);

  /**
   * For rate limited token transfer transactions,
   * returns if the user-inputted amount + the current window
   * usage would exceed the policy limit
   */
  const isTokenTransferOverPolicyLimit = useMemo(() => {
    // Case: Not a rate limited token transfer policy, return false
    if (
      !policy ||
      policy.transactionType !== 'TOKEN_TRANSFER' ||
      !policy.tokenTransferCondition?.amountThreshold ||
      !selectedToken ||
      !amount ||
      !selectedLimitationUsage
    ) {
      return false;
    }
    try {
      const projectedRawAmount =
        BigInt(selectedLimitationUsage.usedAmountRaw) +
        BigInt(toRawTokenUnits(amount, selectedToken.decimals));
      return (
        projectedRawAmount >
        BigInt(policy.tokenTransferCondition.amountThreshold)
      );
    } catch {
      return false;
    }
  }, [amount, policy, selectedLimitationUsage, selectedToken]);

  const manuallySelectedAddresses = useMemo(() => {
    if (transactionType === TransactionTypeEnum.TOKEN_TRANSFER) {
      return selectedRecipient ? [selectedRecipient.address] : [];
    }
    return selectedContract
      ? [
          selectedContract.address,
          ...collectAddressParameterValues(
            functionAbiInputs,
            functionParameters
          ),
        ]
      : [];
  }, [
    transactionType,
    selectedRecipient,
    selectedContract,
    functionAbiInputs,
    functionParameters,
  ]);
  const manualAddressValidation = useManualTransactionAddressValidation(
    manuallySelectedAddresses,
    transactionType === TransactionTypeEnum.CONTRACT_INTERACTION
      ? importSource
      : undefined
  );

  /**
   * Check if the form is valid (all required fields filled)
   */
  const isFormValid = useMemo(() => {
    // Address format alone must not unlock a manually supplied address.
    if (!manualAddressValidation.isValid) return false;
    if (transactionType === TransactionTypeEnum.TOKEN_TRANSFER) {
      return !!(
        selectedToken &&
        amount &&
        selectedRecipient &&
        !isTokenTransferOverPolicyLimit
      );
    }
    // For contract interaction, require both contract and function to be selected
    if (transactionType === TransactionTypeEnum.CONTRACT_INTERACTION) {
      if (isUndecodedRawTransaction) {
        return Boolean(selectedContract && importSource === 'json-file');
      }
      // Block if policy contract ABI fetch failed
      if (policyContractError) {
        return false;
      }
      const paramsValid =
        functionParameters.length === functionAbiInputs.length &&
        functionParameters.every((p) => p.isValid);
      // If function is payable, also check that native token value is valid
      const nativeTokenValid =
        selectedFunction?.stateMutability === 'payable'
          ? nativeTokenValue !== '' &&
            nativeTokenValue !== undefined &&
            !isNaN(parseFloat(nativeTokenValue))
          : true;
      return !!(
        selectedContract &&
        selectedFunction &&
        paramsValid &&
        nativeTokenValid
      );
    }
    return true;
  }, [
    manualAddressValidation.isValid,
    importSource,
    transactionType,
    selectedToken,
    amount,
    selectedRecipient,
    isTokenTransferOverPolicyLimit,
    selectedContract,
    selectedFunction,
    functionParameters,
    functionAbiInputs.length,
    nativeTokenValue,
    policyContractError,
    isUndecodedRawTransaction,
  ]);

  /**
   * Encodes the current live draft for simulation. Returns null when the draft
   * is incomplete or cannot be encoded, keeping the footer action disabled.
   */
  const simulationRequest = useMemo(() => {
    // Case: An incomplete live draft cannot be simulated.
    if (!isFormValid || !account || !network || !policy) {
      return null;
    }

    const baseInput = {
      multichainAccountId: account.id,
      networkId: network,
      description: description ?? '',
    };

    if (transactionType === TransactionTypeEnum.TOKEN_TRANSFER) {
      if (!selectedToken || !selectedRecipient) return null;

      return buildSimulationRequestOrNull({
        ...baseInput,
        children: [
          {
            policyId: policy.id,
            transactionType,
            tokenTransferDetails: {
              displayAmount: amount,
              recipient: selectedRecipient,
              asset: selectedToken,
            },
          },
        ],
      });
    }

    if (!selectedContract) return null;

    return buildSimulationRequestOrNull({
      ...baseInput,
      children: [
        {
          policyId: policy.id,
          transactionType,
          contractInteractionDetails: buildLiveContractInteractionDetails({
            contract: selectedContract,
            selectedFunction,
            functionParameters,
            nativeTokenValue,
            nativeTokenDecimals: nativeTokenDecimalsSelected,
            rawTransaction: importedRawTransaction,
            importSource,
          }),
        },
      ],
    });
  }, [
    account,
    amount,
    description,
    functionParameters,
    importedRawTransaction,
    isFormValid,
    importSource,
    nativeTokenDecimalsSelected,
    nativeTokenValue,
    network,
    policy,
    selectedContract,
    selectedFunction,
    selectedRecipient,
    selectedToken,
    transactionType,
  ]);

  /**
   * On continue button click, save transaction details to store and navigate to next page
   */
  const handleContinue = () => {
    if (!isFormValid) return;

    // Save transaction type to store
    setStoreTransactionType(transactionType);

    // If transaction type is token-transfer, save token transfer details
    if (transactionType === TransactionTypeEnum.TOKEN_TRANSFER) {
      // Validate that all required fields are filled
      if (!selectedToken || !amount || !selectedRecipient) {
        // TODO: Show error message to user
        console.error('Missing required fields for token transfer');
        return;
      }

      // Save token transfer details to store
      setStoreTokenTransferDetails({
        displayAmount: amount,
        recipient: {
          address: selectedRecipient.address,
          label: selectedRecipient.label,
        },
        asset: selectedToken,
      });
    }

    // If transaction type is contract-interaction, save contract interaction details
    if (transactionType === TransactionTypeEnum.CONTRACT_INTERACTION) {
      if (!selectedContract) {
        // TODO: Show error message to user
        console.error('Missing required fields for contract interaction');
        return;
      }

      if (isUndecodedRawTransaction && importedRawTransaction) {
        setStoreContractInteractionDetails(
          buildLiveContractInteractionDetails({
            contract: selectedContract,
            // Opaque calldata has no trustworthy decoded form fields.
            selectedFunction: null,
            functionParameters: [],
            nativeTokenValue: '',
            nativeTokenDecimals: nativeTokenDecimalsSelected,
            rawTransaction: importedRawTransaction,
            importSource,
          })
        );
      } else {
        // Validate that a decodable function and all of its inputs are present.
        if (
          !selectedFunction ||
          functionParameters.length !== functionAbiInputs.length ||
          !functionParameters.every((p) => p.isValid)
        ) {
          // TODO: Show error message to user
          console.error('Missing required fields for contract interaction');
          return;
        }

        // Save decoded contract interaction details to store.
        setStoreContractInteractionDetails(
          buildLiveContractInteractionDetails({
            contract: selectedContract,
            selectedFunction,
            functionParameters,
            nativeTokenValue,
            nativeTokenDecimals: nativeTokenDecimalsSelected,
            rawTransaction: importedRawTransaction,
            importSource,
          })
        );
        setStoreFunctionParameters(functionParameters);
      }
    }

    if (onSave) {
      onSave();
      return;
    }
    // Navigate to next page
    navigateForwardToPage({
      page: TransactionBuilderPageType.DESCRIPTION_PAGE,
    });
  };

  /**
   * Import a WalletConnect transaction
   * Called after the WalletConnect dialog has validated the transaction complies with the selected policy.
   * @param transactionPayload The decoded transaction payload to import.
   */
  const handleWalletConnectTransaction = async (
    transactionPayload: DecodedContractTransactionPayload
  ) => {
    // A late WalletConnect approval must not populate a child the user has left.
    if (
      embedded &&
      useTransactionBuilderStore.getState().editorSessionId !== editorSessionId
    )
      return;
    const currentOrPolicyContract = selectedContract ?? policyContract;
    const isSameContractAsCurrentContext = Boolean(
      currentOrPolicyContract &&
        currentOrPolicyContract.address.toLowerCase() ===
          transactionPayload.contractAddress.toLowerCase()
    );

    // Preserve policy-derived/current allowlists when importing for same contract.
    const allowedProxyFunctions = isSameContractAsCurrentContext
      ? (currentOrPolicyContract?.allowedProxyFunctions ?? [])
      : [];
    const allowedImplementationFunctions = isSameContractAsCurrentContext
      ? (currentOrPolicyContract?.allowedImplementationFunctions ?? [])
      : [];

    const importedContract: Contract = {
      address: transactionPayload.contractAddress,
      contractName: transactionPayload.contractName,
      implementationFunctions: transactionPayload.implementationFunctions,
      proxyFunctions: transactionPayload.proxyFunctions,
      allowedProxyFunctions,
      allowedImplementationFunctions,
    };

    setTransactionType(TransactionTypeEnum.CONTRACT_INTERACTION);
    setStoreTransactionType(TransactionTypeEnum.CONTRACT_INTERACTION);
    setSelectedContract(importedContract);
    setSelectedFunction(transactionPayload.selectedFunction);
    setFunctionParameters(transactionPayload.functionParameters);
    setNativeTokenValue(transactionPayload.nativeTokenValue?.value || '');
    setImportedRawTransaction(null);
    // Imported calldata may reference valid addresses outside the address book.
    setImportSource('walletconnect');
    // Aggregate inputs keep local state; remount them for each new import.
    setRawTransactionImportVersion((version) => version + 1);
    setIsContractFromWalletConnect(true);
    setIsFunctionFromWalletConnect(true);
    setNativeTokenDecimalsSelected(
      transactionPayload.nativeTokenValue?.decimals ?? nativeTokenDecimals
    );

    setStoreContractInteractionDetails({
      address: importedContract.address,
      contractName: importedContract.contractName,
      implementationFunctions: importedContract.implementationFunctions,
      proxyFunctions: importedContract.proxyFunctions,
      allowedProxyFunctions,
      allowedImplementationFunctions,
      selectedFunction: transactionPayload.selectedFunction,
      functionParameters: transactionPayload.functionParameters,
      nativeTokenValue: transactionPayload.nativeTokenValue ?? undefined,
      importSource: 'walletconnect',
    });
    setStoreFunctionParameters(transactionPayload.functionParameters);
    setStoreNativeTokenValue(transactionPayload.nativeTokenValue ?? undefined);
    setWalletConnectPolicyGateError(null);
    await walletConnect.approveTransactionRequest();
    setShowWalletConnectSheet(false);
  };

  /**
   * Fill the editable form from decoded JSON while retaining the exact raw
   * transaction as the source of truth for submission.
   */
  const handleRawTransactionImport = (
    transactionPayload: RawContractTransactionImport,
    rawTransaction: RawContractTransaction,
    source?: 'json-file'
  ) => {
    // A late raw JSON import must not populate a child the user has left.
    if (
      embedded &&
      useTransactionBuilderStore.getState().editorSessionId !== editorSessionId
    )
      return;
    const contractAddress =
      transactionPayload.status === 'decoded'
        ? transactionPayload.details.contractAddress
        : transactionPayload.contractAddress;
    const currentOrPolicyContract = selectedContract ?? policyContract;
    const isSameContractAsCurrentContext = Boolean(
      currentOrPolicyContract &&
        currentOrPolicyContract.address.toLowerCase() ===
          contractAddress.toLowerCase()
    );
    const allowedProxyFunctions = isSameContractAsCurrentContext
      ? (currentOrPolicyContract?.allowedProxyFunctions ?? [])
      : [];
    const allowedImplementationFunctions = isSameContractAsCurrentContext
      ? (currentOrPolicyContract?.allowedImplementationFunctions ?? [])
      : [];

    // Case: ABI metadata is unavailable, so preserve only the raw call details.
    if (transactionPayload.status === 'undecoded') {
      const addressBookLabel = contractAddressBookEntries.find(
        (entry) => entry.address.toLowerCase() === contractAddress.toLowerCase()
      )?.name;
      const importedContract: Contract = {
        address: contractAddress,
        contractName:
          addressBookLabel ||
          (isSameContractAsCurrentContext
            ? currentOrPolicyContract?.contractName
            : undefined) ||
          'Unknown contract',
        implementationFunctions: [],
        proxyFunctions: [],
        allowedProxyFunctions,
        allowedImplementationFunctions,
      };

      setTransactionType(TransactionTypeEnum.CONTRACT_INTERACTION);
      setStoreTransactionType(TransactionTypeEnum.CONTRACT_INTERACTION);
      setSelectedContract(importedContract);
      setSelectedFunction(null);
      setFunctionParameters([]);
      setNativeTokenValue('');
      setNativeTokenDecimalsSelected(nativeTokenDecimals);
      setImportedRawTransaction(rawTransaction);
      setImportSource(source);
      setRawTransactionImportVersion((version) => version + 1);
      setIsContractFromWalletConnect(false);
      setIsFunctionFromWalletConnect(false);

      setStoreContractInteractionDetails({
        ...importedContract,
        functionParameters: [],
        rawTransaction,
        ...(source ? { importSource: source } : {}),
      });
      setStoreNativeTokenValue(undefined);
      return;
    }

    const decodedTransaction = transactionPayload.details;
    const importedContract: Contract = {
      address: decodedTransaction.contractAddress,
      contractName: decodedTransaction.contractName,
      implementationFunctions: decodedTransaction.implementationFunctions,
      proxyFunctions: decodedTransaction.proxyFunctions,
      allowedProxyFunctions,
      allowedImplementationFunctions,
    };

    setTransactionType(TransactionTypeEnum.CONTRACT_INTERACTION);
    setStoreTransactionType(TransactionTypeEnum.CONTRACT_INTERACTION);
    setSelectedContract(importedContract);
    setSelectedFunction(decodedTransaction.selectedFunction);
    setFunctionParameters(decodedTransaction.functionParameters);
    setNativeTokenValue(decodedTransaction.nativeTokenValue?.value ?? '');
    setNativeTokenDecimalsSelected(
      decodedTransaction.nativeTokenValue?.decimals ?? nativeTokenDecimals
    );
    setImportedRawTransaction(rawTransaction);
    setImportSource(source);
    setRawTransactionImportVersion((version) => version + 1);
    setIsContractFromWalletConnect(false);
    setIsFunctionFromWalletConnect(false);

    setStoreContractInteractionDetails({
      ...importedContract,
      selectedFunction: decodedTransaction.selectedFunction,
      functionParameters: decodedTransaction.functionParameters,
      nativeTokenValue: decodedTransaction.nativeTokenValue ?? undefined,
      rawTransaction,
      ...(source ? { importSource: source } : {}),
    });
    setStoreFunctionParameters(decodedTransaction.functionParameters);
    setStoreNativeTokenValue(decodedTransaction.nativeTokenValue ?? undefined);
  };

  const advancedControls = transactionType ===
    TransactionTypeEnum.CONTRACT_INTERACTION && (
    <>
      {/* Keep advanced controls below long argument forms so they cannot obscure inputs. */}
      <div
        className={`z-40 w-[358px] max-w-[calc(100%_-_32px)] rounded-t-[19px] rounded-b-[23px] bg-zinc-100 ring-1 ring-inset ring-white ${embedded ? 'mx-auto mt-[30px] mb-[18px] shrink-0' : 'fixed bottom-24 left-1/2 -translate-x-1/2'}`}
      >
        <div className="flex h-[28px] items-center justify-center px-[20px] py-[7px]">
          <span className="text-[10px] font-medium leading-[14px] text-zinc-400">
            Advanced Contract Interactions
          </span>
        </div>
        <div className="relative grid h-[40px] grid-cols-[minmax(0,200fr)_minmax(0,158fr)] overflow-hidden rounded-[65px] bg-zinc-50 shadow-[0px_4px_6px_-3px_rgba(0,0,0,0.1)] ring-1 ring-inset ring-white">
          <button
            className="flex h-[40px] min-w-0 items-center justify-center gap-[8px] bg-zinc-50 px-[15px] py-[10px] text-zinc-800 transition-colors hover:bg-white focus:outline-none"
            onClick={() => setShowWalletConnectSheet(true)}
          >
            <WalletConnectIcon />
            <span className="whitespace-nowrap text-[11px] font-medium leading-[20px]">
              WalletConnect
            </span>
            <span className="whitespace-nowrap rounded-[100px] bg-zinc-600 px-[7px] py-[5px] text-center text-[10px] font-medium leading-[10px] text-white">
              {walletConnect.dappData.length} dapp
              {walletConnect.dappData.length !== 1 ? 's' : ''}
            </span>
          </button>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-[55.866%] top-0 z-10 h-[40px] w-px bg-zinc-200"
          />
          <button
            className="flex h-[40px] min-w-0 items-center justify-center gap-[8px] bg-zinc-50 px-[15px] py-[10px] text-zinc-800 transition-colors hover:bg-white focus:outline-none"
            onClick={() => setShowRawTransactionImport(true)}
          >
            <Upload className="size-[16px]" />
            <span className="whitespace-nowrap text-[11px] font-medium leading-[20px]">
              Custom JSON File
            </span>
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div
      className={`relative w-full flex flex-col bg-muted ${embedded ? 'min-h-full' : 'min-h-[100dvh]'}`}
    >
      {!embedded && (
        <TransactionBuilderHeader
          title="What do you want your transaction to do?"
          showBackButton={true}
        />
      )}
      <div
        className={`flex flex-col w-full mx-auto gap-[30px] ${embedded ? 'max-w-[738px] flex-1 px-[30px] pt-[18px]' : 'max-w-3xl px-4 py-6'}`}
      >
        {/* Policy Indicator */}
        {policy && (
          <TransactionBuilderPolicyIndicator
            policy={policy}
            {...(embedded
              ? { className: 'w-full max-w-[590px] py-[13px]' }
              : {})}
            limitationUsage={selectedLimitationUsage}
            currentTransactionAmount={amount}
          />
        )}
        {/* Transaction Type Row */}
        <DetailsRow label="Transaction type">
          <div className="bg-zinc-100 border border-solid border-white rounded-[36px] w-[384px]">
            <div className="flex gap-[5px] p-[5px]">
              <TransactionTypeToggleButton
                icon={<ArrowLeftRightIcon className="w-[18px] h-[18px]" />}
                label="Token Transfer"
                isActive={
                  transactionType === TransactionTypeEnum.TOKEN_TRANSFER
                }
                onClick={() =>
                  setTransactionType(TransactionTypeEnum.TOKEN_TRANSFER)
                }
                disabled={isTransactionTypeDisabled(
                  TransactionTypeEnum.TOKEN_TRANSFER
                )}
              />
              <TransactionTypeToggleButton
                icon={<FileJsonIcon className="w-[18px] h-[18px]" />}
                label="Contract Interaction"
                isActive={
                  transactionType === TransactionTypeEnum.CONTRACT_INTERACTION
                }
                onClick={() =>
                  setTransactionType(TransactionTypeEnum.CONTRACT_INTERACTION)
                }
                disabled={isTransactionTypeDisabled(
                  TransactionTypeEnum.CONTRACT_INTERACTION
                )}
              />
            </div>
          </div>
        </DetailsRow>

        {/* Token Transfer Fields - Only shown when token-transfer is selected */}
        {transactionType === TransactionTypeEnum.TOKEN_TRANSFER && (
          <>
            {/* Token Row */}
            <DetailsRow label="Token">
              {isTokenSelectorDisabled ? (
                // Disabled state - token is locked by policy
                <div className="bg-zinc-100 border border-solid border-zinc-200 rounded-[36px] w-[386px] p-[5px] cursor-not-allowed">
                  <div className="flex items-center justify-between pl-[5px] pr-[15px] py-[5px]">
                    <div className="flex items-center justify-between flex-1">
                      <div className="flex items-center gap-[12px]">
                        {selectedToken ? (
                          selectedToken.logoUrl ? (
                            <img
                              src={selectedToken.logoUrl}
                              alt={selectedToken.symbol}
                              className="w-[24px] h-[24px] rounded-full"
                            />
                          ) : (
                            <DefaultTokenIcon size={24} />
                          )
                        ) : null}
                        <p className="font-medium text-[14px] text-zinc-500 px-[12px]">
                          {selectedToken?.symbol || 'Select a token'}
                        </p>
                      </div>
                      {isTokenPricingLoading ? (
                        <div className="flex items-center gap-2 text-[12px] text-zinc-400">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          <span>Loading...</span>
                        </div>
                      ) : selectedToken ? (
                        <p className="text-[12px] text-zinc-400">
                          {formatBalance(
                            getAssetNetworkInfo(selectedToken).balance
                          )}{' '}
                          (
                          {formatUsdValue(
                            getAssetNetworkInfo(selectedToken).usdValue
                          )}
                          )
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : (
                <DropdownButton onClick={() => setShowTokenSelector(true)}>
                  <div className="flex items-center justify-between flex-1">
                    <div className="flex items-center gap-[12px]">
                      {selectedToken ? (
                        selectedToken.logoUrl ? (
                          <img
                            src={selectedToken.logoUrl}
                            alt={selectedToken.symbol}
                            className="w-[24px] h-[24px] rounded-full"
                          />
                        ) : (
                          <DefaultTokenIcon size={24} />
                        )
                      ) : null}
                      <p className="font-medium text-[14px] text-zinc-500 px-[12px]">
                        {selectedToken?.symbol || 'Select a token'}
                      </p>
                    </div>
                    {isTokenPricingLoading ? (
                      <div className="flex items-center gap-2 text-[12px] text-zinc-500">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        <span>Loading...</span>
                      </div>
                    ) : selectedToken ? (
                      <p className="text-[12px] text-zinc-500">
                        {formatBalance(
                          getAssetNetworkInfo(selectedToken).balance
                        )}{' '}
                        (
                        {formatUsdValue(
                          getAssetNetworkInfo(selectedToken).usdValue
                        )}
                        )
                      </p>
                    ) : null}
                  </div>
                </DropdownButton>
              )}
            </DetailsRow>

            {/* Amount Row */}
            <DetailsRow label="Amount">
              <div className="bg-white border border-solid border-zinc-100 rounded-[36px] w-[386px]">
                <div className="flex items-center justify-between pl-[5px] pr-[20px] py-[5px]">
                  <input
                    type="text"
                    value={amount}
                    onChange={(e) => {
                      const newValue = e.target.value;

                      // Allow empty string (user clearing the input)
                      if (newValue === '') {
                        setAmount('');
                        return;
                      }

                      // Only allow numbers and a single decimal point
                      // Regex: optional digits, optional single period, optional digits
                      if (!/^\d*\.?\d*$/.test(newValue)) {
                        return;
                      }

                      // Limit decimal places to token's decimals
                      const decimalIndex = newValue.indexOf('.');
                      if (decimalIndex !== -1 && selectedToken) {
                        const decimalPlaces =
                          newValue.length - decimalIndex - 1;
                        if (decimalPlaces > selectedToken.decimals) {
                          return;
                        }
                      }

                      // If policy has an amount threshold and entered value exceeds it, cap to threshold
                      if (
                        amountThreshold &&
                        newValue !== '' &&
                        !isNaN(parseFloat(newValue)) &&
                        parseFloat(newValue) > parseFloat(amountThreshold)
                      ) {
                        setAmount(amountThreshold);
                      } else {
                        setAmount(newValue);
                      }
                    }}
                    placeholder="Enter an amount"
                    className="bg-white flex-1 px-[12px] py-[6px] rounded-[31px] font-medium text-[14px] text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-600 outline-none border-none"
                  />
                  {amount.trim() !== '' &&
                    selectedToken &&
                    (isTokenPricingLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                    ) : (
                      <p className="text-[12px] text-zinc-500">
                        {formatUsdValue(amountUsdValue)}
                      </p>
                    ))}
                </div>
              </div>
            </DetailsRow>

            {/* Recipient Row */}
            <DetailsRow label="Recipient">
              {isRecipientSelectorDisabled ? (
                // Disabled state - recipient is locked by policy
                <div className="bg-zinc-100 border border-solid border-zinc-200 rounded-[36px] w-[386px] overflow-clip p-[8px] cursor-not-allowed">
                  <div className="flex items-center pl-[5px] pr-[5px] py-0">
                    {selectedRecipient ? (
                      <RecipientSummary
                        recipient={selectedRecipient}
                        disabled
                      />
                    ) : null}
                  </div>
                </div>
              ) : selectedRecipient ? (
                <button
                  className={`bg-white border border-solid border-zinc-100 rounded-[36px] w-[386px] overflow-clip p-[8px] text-left transition-colors ${
                    isRecipientSelectorLoading
                      ? 'cursor-not-allowed bg-zinc-50'
                      : 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-200 dark:hover:border-slate-700'
                  }`}
                  onClick={
                    isRecipientSelectorLoading
                      ? undefined
                      : () => setShowRecipientSelector(true)
                  }
                  disabled={isRecipientSelectorLoading}
                >
                  <div className="flex items-center justify-between gap-[10px] pl-[5px] pr-[5px] py-0">
                    <RecipientSummary
                      recipient={selectedRecipient}
                      disabled={isRecipientSelectorLoading}
                    />
                    <div className="flex items-center gap-[10px] shrink-0">
                      <ChevronDown className="w-[24px] h-[24px] text-black" />
                    </div>
                  </div>
                </button>
              ) : (
                <DropdownButton
                  onClick={() => setShowRecipientSelector(true)}
                  disabled={isRecipientSelectorLoading}
                >
                  <div className="flex items-center px-[12px] rounded-[31px]">
                    <p
                      className={`font-medium text-[14px] ${
                        isRecipientSelectorLoading
                          ? 'text-zinc-400'
                          : 'text-zinc-500'
                      }`}
                    >
                      {isRecipientSelectorLoading
                        ? 'Loading recipient options...'
                        : 'Select a recipient'}
                    </p>
                  </div>
                </DropdownButton>
              )}
            </DetailsRow>
          </>
        )}

        {/* Contract Interaction Fields - Only shown when contract-interaction is selected */}
        {transactionType === TransactionTypeEnum.CONTRACT_INTERACTION && (
          <>
            {/* Contract Row */}
            <DetailsRow
              label="Contract"
              labelAccessory={
                isUndecodedRawTransaction ? (
                  <JsonFileValueSourceIndicator
                    onClear={handleContractCleared}
                  />
                ) : isContractFromWalletConnect ? (
                  <WalletConnectValueSourceIndicator
                    onClear={handleContractCleared}
                  />
                ) : undefined
              }
            >
              {isUndecodedRawTransaction && selectedContract ? (
                <div className="w-[386px] max-w-full overflow-hidden rounded-[36px] border border-zinc-100 bg-white p-[8px] dark:border-zinc-800 dark:bg-zinc-950">
                  <div className="flex items-center justify-between gap-[10px] py-0 pl-[5px] pr-[5px]">
                    <p className="min-w-0 flex-1 truncate text-left text-[14px] font-medium leading-[20px] text-zinc-900 dark:text-white">
                      {selectedContract.contractName}
                    </p>
                    <div className="flex shrink-0 items-center gap-[10px]">
                      <p className="font-mono text-[12px] leading-[24px] text-zinc-500">
                        {formatAddress(selectedContract.address)}
                      </p>
                      <ChevronDown
                        className="h-[24px] w-[24px] text-black"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </div>
              ) : policyContractAddress === null ? (
                // Any contract allowed - show free-form address input
                network ? (
                  <ContractAddressInput
                    network={network}
                    initialContract={selectedContract}
                    onContractLoaded={handleContractLoaded}
                    onContractCleared={handleContractCleared}
                  />
                ) : (
                  // Network not yet available - show placeholder
                  <div className="bg-zinc-100 border border-solid border-zinc-200 rounded-[36px] w-[386px] overflow-clip p-[8px]">
                    <div className="flex items-center pl-[5px] py-0">
                      <p className="font-medium text-[14px] text-zinc-400 leading-[20px]">
                        Loading...
                      </p>
                    </div>
                  </div>
                )
              ) : isPolicyContractLoading ? (
                // Loading state - policy specifies a contract but ABI not yet loaded
                <div className="bg-zinc-100 border border-solid border-zinc-200 rounded-[36px] w-[386px] overflow-clip p-[8px]">
                  <div className="flex items-center pl-[5px] py-0">
                    <p className="font-medium text-[14px] text-zinc-400 leading-[20px]">
                      Loading contract...
                    </p>
                  </div>
                </div>
              ) : policyContractError ? (
                // Error state - ABI fetch failed
                <div className="flex flex-col">
                  <div className="bg-white dark:bg-zinc-950 border border-solid border-red-300 dark:border-red-800 rounded-[36px] w-[386px] overflow-clip p-[8px]">
                    <div className="flex items-center justify-between pl-[5px] pr-[5px] py-0">
                      <p className="font-mono text-[14px] text-zinc-900 dark:text-white leading-[20px]">
                        {policyContractAddress &&
                          formatAddress(policyContractAddress)}
                      </p>
                    </div>
                  </div>
                  <p className="text-red-500 text-[12px] mt-[4px] pl-[12px]">
                    Failed to fetch contract ABI
                  </p>
                </div>
              ) : policyContract ? (
                // Policy specifies exactly one contract - show locked state (no chevron)
                <div className="bg-white dark:bg-zinc-950 border border-solid border-zinc-100 dark:border-zinc-800 rounded-[36px] w-[386px] overflow-clip p-[8px]">
                  <div className="flex items-center justify-between pl-[5px] pr-[5px] py-0">
                    <p className="font-medium text-[14px] text-zinc-900 dark:text-white leading-[20px]">
                      {policyContract.contractName || 'Unknown Contract'}
                    </p>
                    <div className="flex items-center gap-[10px] shrink-0">
                      <p className="font-mono text-[12px] text-zinc-500 leading-[24px]">
                        {formatAddress(policyContract.address)}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </DetailsRow>

            {importedContractAddress &&
            !importedContractAddressBookEntry &&
            !isImportedContractAddressBookLoading &&
            !importedContractAddressBookError ? (
              <div className="-mt-5 w-[386px] max-w-full self-end">
                <ImportedAddressBookNotice
                  address={importedContractAddress}
                  label="contract"
                />
              </div>
            ) : null}

            {isUndecodedRawTransaction && importedRawTransaction ? (
              <UndecodedCalldataPanel
                calldata={importedRawTransaction.data}
                value={importedRawTransaction.value}
                onClear={handleContractCleared}
              />
            ) : null}

            {/* Function Row - Only shown when a contract is selected and ABI loaded successfully */}
            {shouldShowFunctionRow && (
              <DetailsRow
                label="Function"
                labelAccessory={
                  isFunctionFromWalletConnect ? (
                    <WalletConnectValueSourceIndicator
                      onClear={() => {
                        setSelectedFunction(null);
                        setFunctionParameters([]);
                        setImportedRawTransaction(null);
                        setImportSource(undefined);
                        clearStoreFunctionData();
                        setIsFunctionFromWalletConnect(false);
                      }}
                    />
                  ) : undefined
                }
              >
                {isFunctionSelectorDisabled ? (
                  // Disabled state - function is locked by policy
                  <div className="bg-zinc-100 border border-solid border-zinc-200 rounded-[36px] w-[386px] overflow-clip p-[8px] cursor-not-allowed">
                    <div className="flex items-center justify-between pl-[5px] pr-[5px] py-0">
                      <p className="font-medium text-[14px] text-zinc-500 leading-[20px]">
                        {selectedFunction
                          ? selectedFunction.name.length > 24
                            ? `${selectedFunction.name.slice(0, 24)}...`
                            : selectedFunction.name
                          : 'Select a function'}
                      </p>
                      <div className="flex items-center gap-[10px] shrink-0">
                        {selectedFunction && (
                          <p className="text-[12px] text-zinc-400 leading-[24px]">
                            {selectedFunction.inputs.length} argument
                            {selectedFunction.inputs.length !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : selectedFunction ? (
                  <button
                    className="bg-white border border-solid border-zinc-100 rounded-[36px] w-[386px] cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-200 dark:hover:border-slate-700 transition-colors overflow-clip p-[8px]"
                    onClick={() => setShowFunctionSelector(true)}
                  >
                    <div className="flex items-center justify-between pl-[5px] pr-[5px] py-0">
                      {/* Truncate function name after 24 characters and append "..." if longer */}
                      <p className="font-medium text-[14px] text-zinc-900 leading-[20px]">
                        {selectedFunction.name.length > 24
                          ? `${selectedFunction.name.slice(0, 24)}...`
                          : selectedFunction.name}
                      </p>
                      <div className="flex items-center gap-[10px] shrink-0">
                        <p className="text-[12px] text-zinc-500 leading-[24px]">
                          {selectedFunction.inputs.length} argument
                          {selectedFunction.inputs.length !== 1 ? 's' : ''}
                        </p>
                        <ChevronDown className="w-[24px] h-[24px] text-black" />
                      </div>
                    </div>
                  </button>
                ) : (
                  <DropdownButton onClick={() => setShowFunctionSelector(true)}>
                    <div className="flex items-center px-[12px] rounded-[31px]">
                      <p className="font-medium text-[14px] text-zinc-500">
                        Select a function
                      </p>
                    </div>
                  </DropdownButton>
                )}
              </DetailsRow>
            )}

            {/* Native Token Value - Only shown for payable functions */}
            {selectedContract &&
              !policyContractError &&
              selectedFunction &&
              selectedFunction.stateMutability === 'payable' && (
                <DetailsRow label="Native token amount">
                  <div className="bg-white border border-solid border-zinc-100 rounded-[36px] w-[386px]">
                    <div className="flex items-center gap-2 pl-[5px] pr-[12px] py-[5px]">
                      <input
                        type="text"
                        value={nativeTokenValue}
                        onChange={(e) => {
                          const newValue = e.target.value;

                          // Allow empty string
                          if (newValue === '') {
                            setNativeTokenValue('');
                            setImportedRawTransaction(null);
                            clearStoreRawTransaction();
                            setStoreNativeTokenValue(undefined);
                            return;
                          }

                          // Only allow numbers and a single decimal point
                          if (!/^\d*\.?\d*$/.test(newValue)) {
                            return;
                          }

                          // Limit decimal places to selected decimals
                          const decimalIndex = newValue.indexOf('.');
                          if (decimalIndex !== -1) {
                            const decimalPlaces =
                              newValue.length - decimalIndex - 1;
                            if (decimalPlaces > nativeTokenDecimalsSelected) {
                              return;
                            }
                          }

                          // Update local state immediately for responsive input
                          setNativeTokenValue(newValue);
                          setImportedRawTransaction(null);
                          clearStoreRawTransaction();

                          // Also update store
                          const nativeValue: NativeTokenValue = {
                            value: newValue,
                            decimals: nativeTokenDecimalsSelected,
                          };
                          setStoreNativeTokenValue(nativeValue);
                        }}
                        placeholder="0.0"
                        className="bg-white flex-1 px-[12px] py-[6px] rounded-[31px] font-medium text-[14px] text-zinc-900 placeholder:text-zinc-500 dark:placeholder:text-zinc-600 outline-none border-none"
                      />
                      <div className="flex items-center gap-2">
                        <DecimalsButton
                          decimals={nativeTokenDecimalsSelected}
                          formatType="number"
                          hideTimestamp
                          onChange={(_, dec) => {
                            setNativeTokenDecimalsSelected(dec ?? 0);
                            setImportedRawTransaction(null);
                            clearStoreRawTransaction();
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </DetailsRow>
              )}

            {/* Function Parameters */}
            {selectedContract && !policyContractError && selectedFunction && (
              <ContractFunctionParameterInputs
                cardLayout={embedded}
                functionAbiInputs={functionAbiInputs}
                functionParameters={functionParameters}
                networkId={network as NetworkType}
                rawTransactionImportVersion={rawTransactionImportVersion}
                allowUnsavedAddress={Boolean(importSource)}
                onParameterChange={handleParameterChange}
              />
            )}
          </>
        )}
        {manualAddressValidation.errorMessage && (
          <p role="alert" className="text-sm text-destructive break-all">
            {manualAddressValidation.errorMessage}
          </p>
        )}
        {manualAddressValidation.isLoading && (
          <p role="status" className="text-sm text-muted-foreground">
            Checking addresses in the address book…
          </p>
        )}
      </div>
      {embedded && advancedControls}
      <TransactionBuilderFooter
        embedded={embedded}
        onContinue={handleContinue}
        continueButtonText={
          embedded ? (isEditing ? 'Save changes' : 'Add to batch') : 'Continue'
        }
        secondaryAction={
          embedded
            ? {
                label: 'Back to policies',
                icon: <ChevronLeft className="size-[15px]" />,
                onClick: () =>
                  navigateForwardToPage({
                    page: TransactionBuilderPageType.POLICY_SELECTION_PAGE,
                  }),
              }
            : {
                label: 'Simulate Transaction',
                onClick: () => setIsSimulationOpen(true),
                disabled: simulationRequest === null,
                icon: <FlaskConical aria-hidden="true" />,
              }
        }
        continueButtonDisabled={!isFormValid}
      />
      {!embedded && simulationRequest && (
        <TransactionSimulationSheet
          open={isSimulationOpen}
          onOpenChange={setIsSimulationOpen}
          source="draft"
          request={simulationRequest}
        />
      )}
      {!embedded && advancedControls}

      {/* Token Selector Dialog */}
      <TokenSelectorDialog
        open={showTokenSelector}
        onOpenChange={setShowTokenSelector}
        isLoading={isTokenPricingLoading}
        assets={policyFilteredTokens}
        selectedToken={selectedToken}
        onTokenSelect={setSelectedToken}
        getAssetBalance={(asset) => getAssetNetworkInfo(asset).balance}
        getAssetUsdValue={(asset) => getAssetNetworkInfo(asset).usdValue}
        network={network ?? null}
      />

      {/* Recipient Selector Dialog */}
      {policy && network && (
        <RecipientSelectorDialog
          open={showRecipientSelector}
          onOpenChange={setShowRecipientSelector}
          selectedAddress={selectedRecipient?.address || null}
          onAddressSelect={(address, label) =>
            setSelectedRecipient({ address, label })
          }
          policy={policy}
        />
      )}

      {/* Function Selector Dialog */}
      <FunctionSelectorDialog
        open={showFunctionSelector}
        onOpenChange={setShowFunctionSelector}
        contract={selectedContract}
        selectedFunctionSelector={selectedFunction?.selector || null}
        onFunctionSelect={(nextFunction) => {
          // Clear old function data before setting new function
          setFunctionParameters([]);
          setImportedRawTransaction(null);
          setImportSource(undefined);
          clearStoreFunctionData();
          setNativeTokenValue('');
          setStoreNativeTokenValue(undefined);
          setIsFunctionFromWalletConnect(false);

          // Set the new function
          setSelectedFunction(nextFunction);
        }}
        allowedFunctions={allowedFunctions}
      />
      <RawTransactionImportDialog
        open={showRawTransactionImport}
        onOpenChange={setShowRawTransactionImport}
        network={network}
        policyAllowedContractAddress={policyContractAddress ?? null}
        policyAllowedFunctions={policyAllowedFunctionsForImport}
        policyAllowsUndecodedCalldata={policyAllowsUndecodedCalldata}
        onImportTransaction={handleRawTransactionImport}
      />
      {/* WalletConnect Sheet */}
      <WalletConnectSheet
        open={showWalletConnectSheet}
        onOpenChange={setShowWalletConnectSheet}
        dappData={walletConnect.dappData}
        isConnecting={walletConnect.isConnecting}
        policyGateErrorMessage={walletConnectPolicyGateError}
        connectToDapp={walletConnect.connectToDapp}
        cancelPendingConnection={walletConnect.cancelPendingConnection}
        disconnect={walletConnect.disconnect}
      />
      {/* WalletConnect Transaction Import Dialog */}
      <WalletConnectTransactionDialog
        open={
          walletConnect.transactionError !== null ||
          (walletConnect.transactionData !== null &&
            isContractInteractionAllowedByPolicy)
        }
        network={network}
        transactionData={walletConnect.transactionData}
        transactionError={walletConnect.transactionError}
        connectedDappName={incomingTransactionDappName}
        policyAllowedContractAddress={policyContractAddress}
        policyAllowedFunctions={policyAllowedFunctionsForImport}
        onClose={walletConnect.clearTransactionData}
        onReject={walletConnect.rejectTransactionRequest}
        onImportTransaction={handleWalletConnectTransaction}
      />
    </div>
  );
}
